import { 
  getFirestore, 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where,
  onSnapshot,
  writeBatch,
  Timestamp,
  runTransaction,
  orderBy
} from 'firebase/firestore';
import * as creditPaymentService from './creditPaymentService';
import * as sourceService from './sourceService';
import { 
  validateUserId, 
  validateDocId, 
  ValidationError 
} from '../utils/validators';

import { APP_ID } from '../config/constants';
import { checkChange } from '../ledger/effects';
import { splitDebtPayment, cardUsedAfterLoanPayment } from '../finance/debt';

const PLANNED_PAYMENTS_COLLECTION = 'plannedPayments'; // Renamed from plannedCreditPayments

/**
 * Get Firestore instance
 */
const getDb = () => {
  return getFirestore();
};

/**
 * Get planned payments collection reference for a specific user
 */
const getPlannedPaymentsRef = (userId) => {
  const db = getDb();
  return collection(db, 'artifacts', APP_ID, 'users', userId, PLANNED_PAYMENTS_COLLECTION);
};

/**
 * Validate planned payment data
 */
const validatePlannedPaymentData = (data) => {
  if (!data.amount || data.amount <= 0) {
    throw new ValidationError('Số tiền phải lớn hơn 0');
  }
  if (!data.month || data.month < 1 || data.month > 12) {
    throw new ValidationError('Tháng không hợp lệ');
  }
  if (!data.year || data.year < 2020) {
    throw new ValidationError('Năm không hợp lệ');
  }
  if (!data.paymentType || !['credit', 'debt'].includes(data.paymentType)) {
    throw new ValidationError('Loại thanh toán không hợp lệ');
  }
  if (data.paymentType === 'credit' && !data.creditCardId) {
    throw new ValidationError('Vui lòng chọn thẻ tín dụng');
  }
  if (data.paymentType === 'debt' && !data.debtId) {
    throw new ValidationError('Vui lòng chọn khoản nợ');
  }
  if (!data.sourceWalletId) {
    throw new ValidationError('Vui lòng chọn nguồn thanh toán');
  }
};

/**
 * Add new planned payment record (credit card or debt)
 */
export const addPlannedCreditPayment = async (userId, plannedPaymentData) => {
  try {
    // Validate inputs
    validateUserId(userId);
    validatePlannedPaymentData(plannedPaymentData);
    
    const plannedPaymentsRef = getPlannedPaymentsRef(userId);
    
    // Convert plannedDate to Timestamp if needed
    let plannedDate = Timestamp.now();
    if (plannedPaymentData.plannedDate) {
      if (plannedPaymentData.plannedDate instanceof Date) {
        plannedDate = Timestamp.fromDate(plannedPaymentData.plannedDate);
      } else if (typeof plannedPaymentData.plannedDate === 'string') {
        plannedDate = Timestamp.fromDate(new Date(plannedPaymentData.plannedDate));
      } else if (plannedPaymentData.plannedDate.toDate) {
        plannedDate = plannedPaymentData.plannedDate;
      }
    }
    
    // Prepare data based on payment type
    const dataToSave = {
      year: plannedPaymentData.year,
      month: plannedPaymentData.month,
      plannedDate,
      paymentType: plannedPaymentData.paymentType, // 'credit' | 'debt'
      sourceWalletId: plannedPaymentData.sourceWalletId,
      sourceWalletName: plannedPaymentData.sourceWalletName || '',
      amount: Number(plannedPaymentData.amount),
      note: plannedPaymentData.note || '',
      status: 'pending', // pending | confirmed | cancelled
      actualPaymentId: null,
      confirmedAt: null,
      confirmedBy: null,
      plannedBy: userId,
      isRecurring: plannedPaymentData.isRecurring || false,
      recurringDay: plannedPaymentData.recurringDay || new Date().getDate(),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    // Add type-specific fields
    if (plannedPaymentData.paymentType === 'credit') {
      dataToSave.creditCardId = plannedPaymentData.creditCardId;
      dataToSave.creditCardName = plannedPaymentData.creditCardName || '';
    } else if (plannedPaymentData.paymentType === 'debt') {
      dataToSave.debtId = plannedPaymentData.debtId;
      dataToSave.debtName = plannedPaymentData.debtName || '';
    }
    
    const docRef = await addDoc(plannedPaymentsRef, dataToSave);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding planned payment:', error);
    if (error instanceof ValidationError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: error.message };
  }
};

/**
 * Update existing planned payment record
 */
export const updatePlannedCreditPayment = async (userId, plannedPaymentId, updates) => {
  try {
    // Validate inputs
    validateUserId(userId);
    validateDocId(plannedPaymentId, 'Planned Payment');
    
    const db = getDb();
    const plannedPaymentRef = doc(db, 'artifacts', APP_ID, 'users', userId, PLANNED_PAYMENTS_COLLECTION, plannedPaymentId);
    
    // Prepare updates
    const dataToUpdate = {
      ...updates,
      updatedAt: Timestamp.now()
    };
    
    // Convert plannedDate to Timestamp if needed
    if (updates.plannedDate) {
      if (updates.plannedDate instanceof Date) {
        dataToUpdate.plannedDate = Timestamp.fromDate(updates.plannedDate);
      } else if (typeof updates.plannedDate === 'string') {
        dataToUpdate.plannedDate = Timestamp.fromDate(new Date(updates.plannedDate));
      }
    }

    // Convert amount to number
    if (updates.amount) {
      dataToUpdate.amount = Number(updates.amount);
    }

    await updateDoc(plannedPaymentRef, dataToUpdate);
    return { success: true };
  } catch (error) {
    console.error('Error updating planned credit payment:', error);
    if (error instanceof ValidationError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: error.message };
  }
};

/**
 * Delete planned payment record
 */
export const deletePlannedCreditPayment = async (userId, plannedPaymentId) => {
  try {
    // Validate inputs
    validateUserId(userId);
    validateDocId(plannedPaymentId, 'Planned Payment');
    
    const db = getDb();
    const plannedPaymentRef = doc(db, 'artifacts', APP_ID, 'users', userId, PLANNED_PAYMENTS_COLLECTION, plannedPaymentId);
    await deleteDoc(plannedPaymentRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting planned credit payment:', error);
    if (error instanceof ValidationError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: error.message };
  }
};

/**
 * Delete multiple planned payment records
 */
export const deletePlannedCreditPayments = async (userId, plannedPaymentIds) => {
  try {
    validateUserId(userId);
    
    if (!Array.isArray(plannedPaymentIds) || plannedPaymentIds.length === 0) {
      throw new ValidationError('Danh sách ID không hợp lệ');
    }

    const db = getDb();
    const batch = writeBatch(db);

    plannedPaymentIds.forEach(id => {
      const plannedPaymentRef = doc(db, 'artifacts', APP_ID, 'users', userId, PLANNED_PAYMENTS_COLLECTION, id);
      batch.delete(plannedPaymentRef);
    });

    await batch.commit();
    return { success: true, count: plannedPaymentIds.length };
  } catch (error) {
    console.error('Error deleting planned payments:', error);
    if (error instanceof ValidationError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: error.message };
  }
};

/**
 * Subscribe to planned payments real-time updates
 */
export const subscribeToPlannedCreditPayments = (userId, callback) => {
  try {
    const plannedPaymentsRef = getPlannedPaymentsRef(userId);
    const q = query(plannedPaymentsRef, orderBy('plannedDate', 'desc'));
    
    return onSnapshot(q, 
      (snapshot) => {
        const plannedPayments = [];
        snapshot.forEach((doc) => {
          plannedPayments.push({ id: doc.id, ...doc.data() });
        });
        callback({ success: true, data: plannedPayments });
      },
      (error) => {
        console.error('Error subscribing to planned payments:', error);
        callback({ success: false, error: error.message });
      }
    );
  } catch (error) {
    console.error('Error setting up planned payments subscription:', error);
    return () => {}; // Return empty unsubscribe function
  }
};

/**
 * Get planned payments for a specific month/year
 */
export const getPlannedCreditPaymentsByMonth = async (userId, year, month) => {
  try {
    validateUserId(userId);
    
    const plannedPaymentsRef = getPlannedPaymentsRef(userId);
    const q = query(
      plannedPaymentsRef,
      where('year', '==', year),
      where('month', '==', month),
      orderBy('plannedDate', 'asc')
    );
    
    const snapshot = await getDocs(q);
    const plannedPayments = [];
    snapshot.forEach((doc) => {
      plannedPayments.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: plannedPayments };
  } catch (error) {
    console.error('Error getting planned payments by month:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Confirm planned payment (create actual payment/repayment and update balances)
 * Handles both credit card payments and debt repayments
 */
export const confirmPlannedCreditPayment = async (userId, plannedPaymentId, actualData) => {
  try {
    validateUserId(userId);
    validateDocId(plannedPaymentId, 'Planned Payment');
    
    const db = getDb();
    
    return await runTransaction(db, async (transaction) => {
      // 1. Get planned payment data
      const plannedPaymentRef = doc(db, 'artifacts', APP_ID, 'users', userId, PLANNED_PAYMENTS_COLLECTION, plannedPaymentId);
      const plannedPaymentDoc = await transaction.get(plannedPaymentRef);
      
      if (!plannedPaymentDoc.exists()) {
        throw new ValidationError('Dự thanh toán không tồn tại');
      }
      
      const plannedPayment = plannedPaymentDoc.data();
      
      if (plannedPayment.status === 'confirmed') {
        throw new ValidationError('Dự thanh toán đã được xác nhận');
      }
      
      // 2. Prepare actual payment data
      const sourceWalletId = actualData.sourceWalletId || plannedPayment.sourceWalletId;
      const amount = Number(actualData.amount || plannedPayment.amount);
      
      // Handle paymentDate - can be string, Date, or Timestamp
      let paymentDate;
      if (actualData.date) {
        // If date is provided, ensure it's a Date object
        paymentDate = typeof actualData.date === 'string' ? new Date(actualData.date) : actualData.date;
      } else {
        // Use plannedDate from plannedPayment
        paymentDate = plannedPayment.plannedDate.toDate ? plannedPayment.plannedDate.toDate() : new Date(plannedPayment.plannedDate);
      }
      
      const note = actualData.note || plannedPayment.note || '';
      const paymentType = plannedPayment.paymentType; // 'credit' | 'debt'
      
      // 3. Get source wallet to check balance
      const sourceRef = doc(db, 'artifacts', APP_ID, 'users', userId, 'sources', sourceWalletId);
      const sourceDoc = await transaction.get(sourceRef);
      
      if (!sourceDoc.exists()) {
        throw new ValidationError('Ví nguồn không tồn tại');
      }
      
      const sourceWallet = sourceDoc.data();
      const currentBalance = Number(sourceWallet.balance) || 0;
      
      const policy = checkChange(sourceWallet, 'balance', -amount);
      if (!policy.ok) throw new ValidationError(policy.error);
      
      let newPaymentRef;
      
      if (paymentType === 'credit') {
        // Handle Credit Card Payment
        const creditCardId = actualData.creditCardId || plannedPayment.creditCardId;
        
        // 4a. Get credit card to update balance
        const creditCardRef = doc(db, 'artifacts', APP_ID, 'users', userId, 'sources', creditCardId);
        const creditCardDoc = await transaction.get(creditCardRef);
        
        if (!creditCardDoc.exists()) {
          throw new ValidationError('Thẻ tín dụng không tồn tại');
        }
        
        const creditCard = creditCardDoc.data();
        const currentUsed = Number(creditCard.usedCredit) || 0;
        // Trả vượt dư nợ thẻ làm tiền ví mất đi mà không giảm nợ nào — chặn thay vì kẹp về 0 trong im lặng
        if (amount > currentUsed) {
          throw new ValidationError(`Số trả (${amount.toLocaleString('vi-VN')} ₫) lớn hơn dư nợ thẻ ${creditCard.name || ''} (${currentUsed.toLocaleString('vi-VN')} ₫). Giảm số trả hoặc bỏ qua khoản này.`);
        }
        
        // 5a. Create actual credit payment record
        const creditPaymentsRef = collection(db, 'artifacts', APP_ID, 'users', userId, 'creditPayments');
        newPaymentRef = doc(creditPaymentsRef);
        
        transaction.set(newPaymentRef, {
          creditCardId,
          creditCardName: creditCard.name,
          sourceWalletId,
          sourceWalletName: sourceWallet.name,
          amount,
          date: Timestamp.fromDate(paymentDate),
          note,
          createdAt: Timestamp.now(),
          createdBy: userId
        });
        
        // 6a. Update credit card usedCredit (reduce debt)
        const newUsedCredit = Math.max(0, currentUsed - amount); // Decrease usedCredit
        transaction.update(creditCardRef, {
          usedCredit: newUsedCredit,
          updatedAt: Timestamp.now()
        });
        
      } else if (paymentType === 'debt') {
        // Handle Debt Repayment — GHI CÙNG SỔ với debtService.addPayment
        // (subcollection 'payments', tách gốc/lãi). Trước đây ghi vào
        // 'repayments' riêng: vô hình với tab Nợ, bỏ qua lãi → sổ nợ trôi dần.
        const debtId = actualData.debtId || plannedPayment.debtId;

        // 4b. Get debt to update balance
        const debtRef = doc(db, 'artifacts', APP_ID, 'users', userId, 'debts', debtId);
        const debtDoc = await transaction.get(debtRef);

        if (!debtDoc.exists()) {
          throw new ValidationError('Khoản nợ không tồn tại');
        }

        const debt = debtDoc.data();
        // Khoản vay gắn thẻ tín dụng: trả gốc cũng giảm dư nợ thẻ (đọc trước mọi lệnh ghi)
        let linkedCard = null;
        if (debt.creditCardId) {
          const linkedRef = doc(db, 'artifacts', APP_ID, 'users', userId, 'sources', debt.creditCardId);
          const linkedSnap = await transaction.get(linkedRef);
          if (linkedSnap.exists()) linkedCard = { ref: linkedRef, data: linkedSnap.data() };
        }
        const remainingBalance = Number(debt.remainingBalance) || 0;
        const annualRate = Number(debt.interestRate) || 0;

        // Tách lãi/gốc: lãi tháng = dư nợ × lãi suất/12 (dư nợ giảm dần),
        // phần còn lại của khoản trả là gốc — cùng công thức với PaymentModal
        // Lãi thực tế theo sao kê (nếu người dùng nhập) thay cho công thức; kỳ cuối lệch vài đồng được bù vào lãi
        const split = splitDebtPayment(remainingBalance, annualRate, amount, actualData.interest ?? null);
        const interest = split.interest;
        const principalPaid = split.principal;

        if (split.excess > 0) {
          throw new ValidationError(
            `Phần gốc (${principalPaid.toLocaleString('vi-VN')} ₫) vượt quá dư nợ còn lại (${remainingBalance.toLocaleString('vi-VN')} ₫)`
          );
        }

        // 5b. Ghi vào subcollection 'payments' — cùng schema với debtService.addPayment
        const paymentsRef = collection(db, 'artifacts', APP_ID, 'users', userId, 'debts', debtId, 'payments');
        newPaymentRef = doc(paymentsRef);

        transaction.set(newPaymentRef, {
          totalAmount: amount,
          principal: principalPaid,
          interest,
          paymentDate: Timestamp.fromDate(paymentDate),
          note: note || '',
          sourceId: sourceWalletId,
          createdAt: Timestamp.now()
        });

        // 6b. Giảm dư nợ theo PHẦN GỐC (không phải toàn bộ khoản trả)
        const newRemainingBalance = Math.max(0, remainingBalance - principalPaid);
        const newStatus = newRemainingBalance === 0 ? 'paid' : 'active';

        transaction.update(debtRef, {
          remainingBalance: newRemainingBalance,
          status: newStatus,
          updatedAt: Timestamp.now()
        });
        if (linkedCard && principalPaid > 0) {
          transaction.update(linkedCard.ref, { usedCredit: cardUsedAfterLoanPayment(linkedCard.data.usedCredit, principalPaid), updatedAt: Timestamp.now() });
        }

        // 6c. Lãi vay = chi phí tài chính (tự sinh, không trừ ví lần nữa)
        if (interest > 0) {
          const expensesRef = collection(db, 'artifacts', APP_ID, 'users', userId, 'expenses');
          transaction.set(doc(expensesRef), {
            amount: interest,
            category: 'Chi phí tài chính',
            categoryName: 'Chi phí tài chính',
            description: `Lãi vay ${debt.lender || ''} (tự động từ xác nhận dự thanh toán)`,
            transactionDate: Timestamp.fromDate(paymentDate),
            month: paymentDate.getMonth() + 1,
            year: paymentDate.getFullYear(),
            autoGenerated: true,
            relatedDebtId: debtId,
            relatedPaymentId: newPaymentRef.id,
            createdAt: Timestamp.now()
          });
        }
      }
      
      // 7. Update source wallet balance (reduce money) - common for both types
      const newSourceBalance = currentBalance - amount;
      transaction.update(sourceRef, {
        balance: newSourceBalance,
        updatedAt: Timestamp.now()
      });
      
      // 8. Update planned payment status
      transaction.update(plannedPaymentRef, {
        status: 'confirmed',
        confirmedAmount: amount, // số đã thực trả: mẫu "theo số tháng trước" lấy số này cho kỳ sau
        actualPaymentId: newPaymentRef.id,
        confirmedAt: Timestamp.now(),
        confirmedBy: userId,
        updatedAt: Timestamp.now()
      });
      
      return { 
        success: true, 
        paymentId: newPaymentRef.id,
        message: 'Xác nhận thanh toán thành công'
      };
    });
    
  } catch (error) {
    console.error('Error confirming planned credit payment:', error);
    if (error instanceof ValidationError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: error.message };
  }
};

/**
 * Create recurring payments for next month (both credit and debt)
 */
export const createRecurringCreditPayments = async (userId, targetYear, targetMonth) => {
  try {
    validateUserId(userId);
    
    const db = getDb();
    
    // Calculate previous month
    let prevYear = targetYear;
    let prevMonth = targetMonth - 1;
    if (prevMonth < 1) {
      prevMonth = 12;
      prevYear--;
    }
    
    // Get recurring planned payments from previous month
    const plannedPaymentsRef = getPlannedPaymentsRef(userId);
    const q = query(
      plannedPaymentsRef,
      where('year', '==', prevYear),
      where('month', '==', prevMonth),
      where('isRecurring', '==', true)
    );
    
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return {
        success: true,
        count: 0,
        message: 'Không có thanh toán định kỳ nào từ tháng trước'
      };
    }

    // Chống trùng: đọc các plan đã có của THÁNG ĐÍCH — chạy hai lần không
    // được sinh hai bộ; mỗi khoản nợ/thẻ chỉ một plan định kỳ mỗi tháng
    const existingQ = query(
      plannedPaymentsRef,
      where('year', '==', targetYear),
      where('month', '==', targetMonth)
    );
    const existingSnap = await getDocs(existingQ);
    const existingKeys = new Set(
      existingSnap.docs.map(d => {
        const p = d.data();
        return `${p.paymentType}:${p.creditCardId || p.debtId || ''}`;
      })
    );

    const batch = writeBatch(db);
    let count = 0;
    let skipped = 0;

    snapshot.forEach((docSnap) => {
      const recurringPayment = docSnap.data();

      // Bỏ qua plan đã HUỶ — người dùng huỷ nghĩa là không muốn nó lặp lại
      if (recurringPayment.status === 'cancelled') { skipped++; return; }

      // Bỏ qua nếu tháng đích đã có plan cho cùng khoản nợ/thẻ
      const key = `${recurringPayment.paymentType}:${recurringPayment.creditCardId || recurringPayment.debtId || ''}`;
      if (existingKeys.has(key)) { skipped++; return; }
      existingKeys.add(key);

      // Calculate new planned date
      const newPlannedDate = new Date(targetYear, targetMonth - 1, recurringPayment.recurringDay);
      
      // Create new planned payment for target month
      const newPaymentRef = doc(collection(db, 'artifacts', APP_ID, 'users', userId, PLANNED_PAYMENTS_COLLECTION));
      
      const newData = {
        year: targetYear,
        month: targetMonth,
        plannedDate: Timestamp.fromDate(newPlannedDate),
        paymentType: recurringPayment.paymentType,
        sourceWalletId: recurringPayment.sourceWalletId,
        sourceWalletName: recurringPayment.sourceWalletName,
        amount: recurringPayment.amount,
        note: recurringPayment.note,
        status: 'pending',
        actualPaymentId: null,
        confirmedAt: null,
        confirmedBy: null,
        plannedBy: userId,
        isRecurring: true,
        recurringDay: recurringPayment.recurringDay,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // Add type-specific fields
      if (recurringPayment.paymentType === 'credit') {
        newData.creditCardId = recurringPayment.creditCardId;
        newData.creditCardName = recurringPayment.creditCardName;
      } else if (recurringPayment.paymentType === 'debt') {
        newData.debtId = recurringPayment.debtId;
        newData.debtName = recurringPayment.debtName;
      }
      
      batch.set(newPaymentRef, newData);
      count++;
    });
    
    if (count > 0) await batch.commit();

    return {
      success: true,
      count,
      message: count > 0
        ? `Đã tạo ${count} thanh toán định kỳ cho tháng ${targetMonth}/${targetYear}${skipped > 0 ? ` (bỏ qua ${skipped} đã có/đã huỷ)` : ''}`
        : 'Không có gì để tạo — các thanh toán định kỳ tháng này đã tồn tại hoặc đã huỷ'
    };
    
  } catch (error) {
    console.error('Error creating recurring payments:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Cancel planned payment
 */
export const cancelPlannedCreditPayment = async (userId, plannedPaymentId) => {
  try {
    validateUserId(userId);
    validateDocId(plannedPaymentId, 'Planned Payment');
    
    const db = getDb();
    const plannedPaymentRef = doc(db, 'artifacts', APP_ID, 'users', userId, PLANNED_PAYMENTS_COLLECTION, plannedPaymentId);
    
    await updateDoc(plannedPaymentRef, {
      status: 'cancelled',
      updatedAt: Timestamp.now()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error cancelling planned credit payment:', error);
    return { success: false, error: error.message };
  }
};
